import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Check } from "lucide-react";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import {
  approveProviderCode,
  getJoinInvite,
  registerWithCode,
} from "../../../services/CareTeamService";
import { signInWithPassword } from "../../../services/authSession";
import PasswordInput from "../../common/PasswordInput";
import { afterSignInPath } from "../../User/SignInPage/SignInPage";
import { apiErrorMessage, codeErrorText, shortName } from "../careUtils";
import { icon } from "../ui";
import ConsentDetails from "./ConsentDetails";
import "../careTeam.css";

const FormError = ({ text }) =>
  text ? (
    <p className="ct-form-error" role="alert">
      {icon(AlertTriangle)}
      <span>{text}</span>
    </p>
  ) : null;

export const deadInviteText = (error) =>
  error?.error === "expired"
    ? `This invite from ${error.provider_name} has expired. Please ask them for a new one.`
    : "We couldn't find this invite. It may have been used already or cancelled. " +
      "Please ask your provider for a new one.";

const failureText = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.error === "already_connected") return codeErrorText(data);
  if (typeof data?.error === "string") return deadInviteText(data);
  return apiErrorMessage(err, fallback);
};

const Field = ({ id, label, optional, children }) => (
  <div className="ct-field">
    <label htmlFor={id}>
      {label} {optional && <span className="ct-optional">(optional)</span>}
    </label>
    {children}
  </div>
);

// New to MendGrove: the details the provider typed are filled in, and the
// account shares with them as soon as it's made.
const NewAccountForm = ({ code, patient, onCreated, onDecline }) => {
  const [form, setForm] = useState({
    name: patient.name || "",
    email: patient.email || "",
    password: "",
    re_password: "",
    birth_year: patient.year_of_birth ? String(patient.year_of_birth) : "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!form.email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    if (form.password !== form.re_password) {
      setError("The two passwords don't match.");
      return;
    }
    const year = form.birth_year.trim();
    if (year && !/^\d{4}$/.test(year)) {
      setError("Please enter your birth year, like 1951, or leave it blank.");
      return;
    }
    setBusy(true);
    try {
      const session = await registerWithCode({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        re_password: form.re_password,
        birth_year: year ? Number(year) : null,
        code,
      });
      onCreated(session);
    } catch (err) {
      setError(failureText(err, "We couldn't create your account. Please try again."));
      setBusy(false);
    }
  };

  return (
    <form className="ct-stack ct-join__form" onSubmit={submit}>
      <Field id="join-name" label="Your name">
        <input
          id="join-name"
          className="ct-input"
          autoComplete="name"
          value={form.name}
          onChange={set("name")}
        />
      </Field>
      <Field id="join-email" label="Email">
        <input
          id="join-email"
          className="ct-input"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={set("email")}
        />
      </Field>
      <Field id="join-password" label="Password">
        <PasswordInput
          id="join-password"
          className="ct-input"
          autoComplete="new-password"
          value={form.password}
          onChange={set("password")}
        />
      </Field>
      <Field id="join-password-again" label="Repeat password">
        <PasswordInput
          id="join-password-again"
          className="ct-input"
          autoComplete="new-password"
          value={form.re_password}
          onChange={set("re_password")}
        />
      </Field>
      <Field id="join-birth-year" label="Birth year" optional>
        <input
          id="join-birth-year"
          className="ct-input"
          inputMode="numeric"
          maxLength={4}
          placeholder="e.g. 1951"
          value={form.birth_year}
          onChange={set("birth_year")}
        />
      </Field>
      <FormError text={error} />
      <div className="ct-consent-actions">
        <button type="submit" className="ct-btn ct-btn--primary" disabled={busy}>
          {busy ? "Creating your account…" : "Create my account and share"}
        </button>
        <button type="button" className="ct-btn ct-btn--secondary" onClick={onDecline}>
          No, thank you
        </button>
      </div>
    </form>
  );
};

// Already plays: sign in and agree in the same step.
const SignInForm = ({ onSignedIn, onDecline }) => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    let session;
    try {
      session = await signInWithPassword(identifier.trim(), password);
    } catch (err) {
      setError(
        err?.response?.status === 401
          ? "That username or email and password don't match an account."
          : apiErrorMessage(err, "We couldn't sign you in. Please try again."),
      );
      setBusy(false);
      return;
    }
    if (session.user?.role === "clinician") {
      setError("That's a provider account. Please sign in with your own player account.");
      setBusy(false);
      return;
    }
    onSignedIn(session);
  };

  return (
    <form className="ct-stack ct-join__form" onSubmit={submit}>
      <Field id="join-identifier" label="Username or email">
        <input
          id="join-identifier"
          className="ct-input"
          autoComplete="username"
          autoCapitalize="none"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
      </Field>
      <Field id="join-signin-password" label="Password">
        <PasswordInput
          id="join-signin-password"
          className="ct-input"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <FormError text={error} />
      <div className="ct-consent-actions">
        <button type="submit" className="ct-btn ct-btn--primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in and share"}
        </button>
        <button type="button" className="ct-btn ct-btn--secondary" onClick={onDecline}>
          No, thank you
        </button>
      </div>
    </form>
  );
};

// /join/:code — the link (and QR code) on a provider's invite. It shows who
// is asking and what they would see, then the person agrees in one step:
// create an account, sign in, or, already signed in, just say yes.
const JoinPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, isSignedIn, isClinician, login, logout } = useContext(AuthContext);
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [mode, setMode] = useState("new");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    let alive = true;
    getJoinInvite(code)
      .then((data) => alive && setInvite(data))
      .catch((err) => {
        if (!alive) return;
        // Only the server can say an invite is dead. Anything else (no
        // connection, a server error, too many tries) says nothing about it.
        const data = err?.response?.data;
        if (data?.error) setLoadError(data);
        else setLoadFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [code, loadAttempt]);

  const retryLoad = () => {
    setLoadFailed(false);
    setLoadAttempt((n) => n + 1);
  };

  const sharedMessage = () =>
    `You're now sharing your progress with ${invite.provider.name}.`;

  const finish = (who) =>
    navigate(afterSignInPath(who || user || {}), {
      state: { careMessage: sharedMessage() },
    });

  const accept = async (who) => {
    setError("");
    setBusy(true);
    try {
      await approveProviderCode(invite.code);
    } catch (err) {
      if (err?.response?.data?.error !== "already_connected") {
        setError(failureText(err, "That didn't work. Please try again."));
        setBusy(false);
        return;
      }
    }
    finish(who);
  };

  const onCreated = ({ access, refresh, user: created }) => {
    login(created, access, refresh);
    finish(created);
  };

  const onSignedIn = ({ access, refresh, user: signedIn }) => {
    login(signedIn, access, refresh);
    accept(signedIn);
  };

  const decline = () => setDeclined(true);

  let body;
  if (loadError) {
    body = (
      <>
        <h1>This invite can't be used</h1>
        <p className="ct-intro">{deadInviteText(loadError)}</p>
        <button
          type="button"
          className="ct-btn ct-btn--secondary"
          onClick={() => navigate("/home")}
        >
          Go to the games
        </button>
      </>
    );
  } else if (loadFailed) {
    body = (
      <>
        <h1>We couldn't load this invite</h1>
        <p className="ct-intro">
          Your invite may still be fine. Please check your connection and try again.
        </p>
        <button type="button" className="ct-btn ct-btn--primary" onClick={retryLoad}>
          Try again
        </button>
      </>
    );
  } else if (!invite) {
    body = <p className="ct-loading">Loading your invite…</p>;
  } else if (declined) {
    body = (
      <>
        <h1>Nothing was shared</h1>
        <p className="ct-intro">
          That's fine. {invite.provider.name} won't see anything. If you change your
          mind, open this link again.
        </p>
        <button
          type="button"
          className="ct-btn ct-btn--primary"
          onClick={() => navigate("/home")}
        >
          Go to the games
        </button>
      </>
    );
  } else {
    const { provider } = invite;
    let action;
    if (isSignedIn && isClinician) {
      action = (
        <p className="ct-control-box">
          You're signed in to the provider portal. To accept this invite, open the
          link on the patient's own device, or sign out first.
        </p>
      );
    } else if (isSignedIn && invite.already_connected) {
      action = (
        <>
          <p className="ct-success">
            {icon(Check)}
            You're already sharing with {provider.name}.
          </p>
          <button
            type="button"
            className="ct-btn ct-btn--primary"
            onClick={() => navigate("/home")}
          >
            Go to the games
          </button>
        </>
      );
    } else if (isSignedIn) {
      action = (
        <>
          <FormError text={error} />
          <div className="ct-consent-actions">
            <button
              type="button"
              className="ct-btn ct-btn--primary"
              onClick={() => accept(user)}
              disabled={busy}
            >
              Yes, share my progress
            </button>
            <button
              type="button"
              className="ct-btn ct-btn--secondary"
              onClick={decline}
              disabled={busy}
            >
              No, thank you
            </button>
          </div>
          <p className="ct-join__not-you">
            Signed in as {user?.first_name || user?.username}. Not you?{" "}
            <button type="button" className="ct-link-button" onClick={logout}>
              Sign out
            </button>
          </p>
        </>
      );
    } else {
      action = (
        <section className="ct-section ct-join__account">
          <h2>{mode === "new" ? "Create your account" : "Sign in"}</h2>
          <div className="ct-seg ct-join__modes">
            <button
              type="button"
              className="ct-seg__opt"
              aria-pressed={mode === "new"}
              onClick={() => setMode("new")}
            >
              I'm new to MendGrove
            </button>
            <button
              type="button"
              className="ct-seg__opt"
              aria-pressed={mode === "existing"}
              onClick={() => setMode("existing")}
            >
              I already have an account
            </button>
          </div>
          <FormError text={error} />
          {mode === "new" ? (
            <NewAccountForm
              code={invite.code}
              patient={invite.patient}
              onCreated={onCreated}
              onDecline={decline}
            />
          ) : (
            <SignInForm onSignedIn={onSignedIn} onDecline={decline} />
          )}
        </section>
      );
    }
    body = (
      <>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="ct-kicker">An invitation</span>
          <h1>Share your progress with {shortName(provider.name)}?</h1>
        </div>
        <p className="ct-intro">
          {provider.name} invited you to MendGrove, a set of gentle brain-training
          games, and would like to follow how you're doing.
        </p>
        <ConsentDetails provider={provider} />
        {action}
      </>
    );
  }

  return (
    <div className="ct ct-player ct-join">
      <Helmet>
        <title>Your invitation – Mend Grove</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="ct-player__inner ct-player__inner--consent">{body}</div>
    </div>
  );
};

export default JoinPage;
